import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  X,
  FileText,
  Printer,
  Save,
  Download,
  ShoppingCart,
  Truck,
  CreditCard,
  ClipboardCheck,
  Loader2,
  CheckCircle,
  Eye,
  Edit3,
} from "lucide-react";
import api from "../api/axiosInstance";
import { triggerSafeDelete } from "./deleteInterceptor";

const PurchaseOrder = () => {
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [toast, setToast] = useState(null);

  const initialPoState = {
    vendor_id: "",
    project_id: "",
    po_no: "",
    po_date: new Date().toISOString().split("T")[0],
    delivery_date: "",
    delivery_address: "",
    shipping_method: "",
    packaging_instructions: "",
    payment_terms: "Advance",
    payment_mode: "Bank Transfer",
    gst_rate: 18,
    discount_amount: 0,
    shipping_charges: 0,
    items: [
      {
        id: Date.now(),
        item_name: "",
        sku: "",
        qty: 1,
        unit: "pcs",
        unit_price: 0,
      },
    ],
    terms_conditions:
      "1. Material must match approved samples.\n2. Penalty of 2% per week for late delivery.\n3. Return if damaged during transit.",
    notes: "",
  };

  const [po, setPo] = useState(initialPoState);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [vRes, pRes, poRes] = await Promise.all([
        api.get("get_directory.php?type=vendor"),
        api.get("get_projects.php"),
        api.get("get_purchase_orders.php"),
      ]);
      setVendors(vRes.data.data || []);
      setProjects(pRes.data.projects || []);
      setPos(poRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNextPoNo = async () => {
    try {
      const res = await api.get("get_next_po_no.php");
      if (res.data.status === "success") {
        setPo(prev => ({ ...prev, po_no: res.data.next_po_no }));
      }
    } catch (err) {
      console.error("Auto-gen failed", err);
    }
  };

  const handleCreateClick = () => {
    setIsEdit(false);
    setPo(initialPoState);
    fetchNextPoNo();
    setActiveModal("generate_pdf");
  };

  const handleEdit = (item) => {
    setIsEdit(true);
    const parsedItems = typeof item.items_json === 'string' ? JSON.parse(item.items_json) : (item.items || []);
    setPo({ ...item, items: parsedItems });
    setActiveModal("generate_pdf");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this Purchase Order permanently?")) return;
    try {
      await api.post("manage_purchase_order.php", { action: "delete", id });
      showToast("PO Deleted Successfully");
      loadData();
    } catch (err) {
      alert("Delete Failed");
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const calculateSummary = () => {
    const subtotal = po.items.reduce(
      (sum, item) =>
        sum + parseFloat(item.qty || 0) * parseFloat(item.unit_price || 0),
      0,
    );
    const taxable = subtotal - parseFloat(po.discount_amount || 0);
    const totalGst = (taxable * parseFloat(po.gst_rate || 0)) / 100;
    const grandTotal =
      taxable + totalGst + parseFloat(po.shipping_charges || 0);
    return { subtotal, cgst: totalGst / 2, sgst: totalGst / 2, grandTotal };
  };

  const { subtotal, cgst, sgst, grandTotal } = calculateSummary();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      const payload = {
        ...po,
        action: isEdit ? "update" : "create",
        subtotal,
        cgst_amount: cgst,
        sgst_amount: sgst,
        grand_total: grandTotal,
      };
      const res = await api.post("save_purchase_order.php", payload);
      if (res.data.status === "success") {
        if (activeModal === "generate_pdf" && !isEdit) {
          await downloadPO_PDF(res.data.po_id, po.po_no);
        }
        showToast(isEdit ? "PO Updated!" : "Purchase Order Saved!");
        setActiveModal(null);
        loadData();
      }
    } catch (err) {
      alert(err.response?.data?.message || "PO Save Failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const downloadPO_PDF = async (id, no) => {
    try {
      const res = await api.get(`generate_po_pdf.php?id=${id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `PO_${no.replace(/\//g, "_")}.pdf`);
      link.click();
    } catch (err) {
      showToast("PDF Download Failed");
    }
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse text-slate-300 uppercase">
        Syncing Procurement...
      </div>
    );

  return (
    <div className="space-y-8 p-4">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-black text-slate-900 italic uppercase tracking-tighter">
          Procurement / PO
        </h1>
        <button
          onClick={handleCreateClick}
          className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-indigo-700 transition-all"
        >
          <Plus size={18} /> Create Purchase Order
        </button>
      </div>

      {/* List Table */}
      <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] font-black uppercase tracking-widest border-b">
            <tr>
              <th className="p-6">PO Details</th>
              <th className="p-6">Vendor</th>
              <th className="p-6 text-right">Amount</th>
              <th className="p-6 text-center">Status</th>
              <th className="p-6 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {pos.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-slate-50/50 group transition-all"
              >
                <td className="p-6">
                  <p className="font-black text-slate-900">{item.po_no}</p>
                  <p className="text-[10px] text-slate-400 font-bold">
                    {item.po_date}
                  </p>
                </td>
                <td className="p-6">
                  <p className="font-bold text-slate-700 uppercase text-xs">
                    {item.vendor_name}
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Site: {item.project_name}
                  </p>
                </td>
                <td className="p-6 text-right font-black text-slate-900">
                  ₹{parseFloat(item.grand_total).toLocaleString()}
                </td>
                <td className="p-6 text-center">
                  <span className="bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[9px] font-black uppercase">
                    {item.status}
                  </span>
                </td>
                <td className="p-6 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => handleEdit(item)} className="p-2 text-slate-400 hover:text-indigo-600 transition-all">
                      <Edit3 size={18} />
                    </button>
                    <button
                      onClick={() => downloadPO_PDF(item.id, item.po_no)}
                      className="p-2 text-slate-400 hover:text-indigo-600 transition-all"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => triggerSafeDelete(
                        "purchase_orders",
                        item.id,
                        `Purchase Order ${item.po_no} for ₹${item.grand_total} on ${item.po_date}`,
                        () => loadData()
                      )}
                      className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {activeModal && (
        <div className="fixed inset-0 z-[300] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={handleSubmit}
            className="bg-white w-full max-w-5xl rounded-[48px] shadow-2xl p-12 space-y-8 overflow-y-auto max-h-[90vh]"
          >
            <div className="flex justify-between items-center border-b pb-6">
              <h2 className="text-3xl font-black italic uppercase tracking-tighter text-slate-900">
                {isEdit ? "Edit Purchase Order" : "New Purchase Order"}
              </h2>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                className="p-2 bg-slate-50 rounded-full hover:bg-rose-50 text-rose-500"
              >
                <X />
              </button>
            </div>

            {/* Step 1: Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-slate-50 p-8 rounded-[32px]">
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  PO Number
                </label>
                <input
                  readOnly={!isEdit}
                  className="w-full bg-white p-3 rounded-xl font-bold outline-none"
                  value={po.po_no}
                  onChange={(e) => setPo({ ...po, po_no: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  Vendor
                </label>
                <select
                  required
                  className="w-full bg-white p-3 rounded-xl font-bold"
                  value={po.vendor_id}
                  onChange={(e) => setPo({ ...po, vendor_id: e.target.value })}
                >
                  <option value="">Select Vendor</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  Site Project
                </label>
                <select
                  required
                  className="w-full bg-white p-3 rounded-xl font-bold"
                  value={po.project_id}
                  onChange={(e) => setPo({ ...po, project_id: e.target.value })}
                >
                  <option value="">Select Site</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.project_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  Delivery Date
                </label>
                <input
                  type="date"
                  className="w-full bg-white p-3 rounded-xl font-bold"
                  value={po.delivery_date}
                  onChange={(e) =>
                    setPo({ ...po, delivery_date: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Step 2: Items */}
            <div className="space-y-4">
              <h3 className="text-[10px] font-black uppercase text-indigo-500 flex items-center gap-2">
                <ShoppingCart size={14} /> Material Requirements
              </h3>
              {po.items.map((item, idx) => (
                <div
                  key={item.id}
                  className="grid grid-cols-12 gap-3 items-end animate-in fade-in duration-300"
                >
                  <div className="col-span-5">
                    <input
                      placeholder="Item Description"
                      className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm"
                      value={item.item_name}
                      onChange={(e) => {
                        const n = [...po.items];
                        n[idx].item_name = e.target.value;
                        setPo({ ...po, items: n });
                      }}
                    />
                  </div>
                  <div className="col-span-1">
                    <input
                      placeholder="SKU"
                      className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm"
                      value={item.sku}
                      onChange={(e) => {
                        const n = [...po.items];
                        n[idx].sku = e.target.value;
                        setPo({ ...po, items: n });
                      }}
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Qty"
                      className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm"
                      value={item.qty}
                      onChange={(e) => {
                        const n = [...po.items];
                        n[idx].qty = e.target.value;
                        setPo({ ...po, items: n });
                      }}
                    />
                  </div>
                  <div className="col-span-1">
                    <select
                      className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-[10px]"
                      value={item.unit}
                      onChange={(e) => {
                        const n = [...po.items];
                        n[idx].unit = e.target.value;
                        setPo({ ...po, items: n });
                      }}
                    >
                      <option>pcs</option>
                      <option>sqft</option>
                      <option>kg</option>
                      <option>mtr</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Price"
                      className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-sm"
                      value={item.unit_price}
                      onChange={(e) => {
                        const n = [...po.items];
                        n[idx].unit_price = e.target.value;
                        setPo({ ...po, items: n });
                      }}
                    />
                  </div>
                  <div className="col-span-1 text-center py-4">
                    <button
                      type="button"
                      onClick={() =>
                        setPo({
                          ...po,
                          items: po.items.filter((i) => i.id !== item.id),
                        })
                      }
                      className="text-rose-400 hover:text-rose-600"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setPo({
                    ...po,
                    items: [
                      ...po.items,
                      {
                        id: Date.now(),
                        item_name: "",
                        sku: "",
                        qty: 1,
                        unit: "pcs",
                        unit_price: 0,
                      },
                    ],
                  })
                }
                className="text-[10px] font-black text-indigo-600 uppercase flex items-center gap-2 p-4 border-2 border-dashed border-indigo-100 rounded-3xl w-full justify-center hover:bg-indigo-50"
              >
                Add Material Line
              </button>
            </div>

            {/* Step 3: Logistics & Payment */}
            <div className="grid grid-cols-2 gap-10">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2">
                    <Truck size={14} /> Logistics & Address
                  </h3>
                  <textarea
                    className="w-full bg-slate-50 p-4 rounded-3xl font-medium text-sm h-24"
                    placeholder="Full Delivery Site Address"
                    value={po.delivery_address}
                    onChange={(e) =>
                      setPo({ ...po, delivery_address: e.target.value })
                    }
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      className="w-full bg-slate-50 p-3 rounded-2xl font-bold text-xs"
                      placeholder="Shipping Method"
                      value={po.shipping_method}
                      onChange={(e) =>
                        setPo({ ...po, shipping_method: e.target.value })
                      }
                    />
                    <input
                      className="w-full bg-slate-50 p-3 rounded-2xl font-bold text-xs"
                      placeholder="Packaging Instructions"
                      value={po.packaging_instructions}
                      onChange={(e) =>
                        setPo({ ...po, packaging_instructions: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-2">
                    <CreditCard size={14} /> Payment Terms
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      className="p-3 bg-slate-50 rounded-2xl font-bold text-xs"
                      value={po.payment_terms}
                      onChange={(e) =>
                        setPo({ ...po, payment_terms: e.target.value })
                      }
                    >
                      <option>Net 0</option>
                      <option>Net 15</option>
                      <option>Net 30</option>
                      <option>Advance</option>
                      <option>Net 45</option>
                    </select>
                    <select
                      className="p-3 bg-slate-50 rounded-2xl font-bold text-xs"
                      value={po.payment_mode}
                      onChange={(e) =>
                        setPo({ ...po, payment_mode: e.target.value })
                      }
                    >
                      <option>Bank Transfer</option>
                      <option>Cheque</option>
                      <option>UPI</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 text-white p-10 rounded-[48px] space-y-4">
                <div className="flex justify-between opacity-60 text-[10px] font-black uppercase">
                  <span>Subtotal</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center bg-white/5 p-3 rounded-xl text-[10px] font-bold">
                  <span>GST Rate</span>
                  <select
                    className="bg-transparent font-black"
                    value={po.gst_rate}
                    onChange={(e) => setPo({ ...po, gst_rate: e.target.value })}
                  >
                    <option value="18">18%</option>
                    <option value="12">12%</option>
                    <option value="5">5%</option>
                    <option value="28">28%</option>
                  </select>
                </div>
                <div className="flex justify-between opacity-60 text-[10px] font-black uppercase">
                  <span>CGST / SGST</span>
                  <span>₹{cgst.toLocaleString()} x 2</span>
                </div>
                <div className="flex justify-between items-center text-[10px] font-black uppercase">
                  <span>Shipping</span>
                  <input
                    type="number"
                    className="bg-white/10 w-24 p-2 rounded-lg text-right"
                    value={po.shipping_charges}
                    onChange={(e) =>
                      setPo({ ...po, shipping_charges: e.target.value })
                    }
                  />
                </div>
                <div className="border-t border-white/10 pt-6 flex justify-between items-center">
                  <span className="text-sm font-black uppercase italic text-indigo-400">
                    Total Value
                  </span>
                  <span className="text-4xl font-black italic tracking-tighter">
                    ₹{grandTotal.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <button
              disabled={saveLoading}
              className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl flex items-center justify-center gap-3 hover:bg-indigo-600 transition-all"
            >
              {saveLoading ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Printer size={24} />
              )}
              {isEdit ? "Update Purchase Order" : "Generate & Send Purchase Order"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrder;